import request from "../axios-instance";
import { uploadIxdToStagingSlot } from "../ixd-upload/policy";
import { ApiError } from "../errors";
import { getAuthIdToken } from "@/lib/firebase";
import type {
  IxdDsdCompareLogItem,
  ComparisonSummaryResponse,
  ChunkDetailResponse,
  IxdPreviewDiffChunk,
} from "./types";

export type {
  DsdCellMatchStatus,
  DsdParsedCell,
  DsdSentenceMatch,
  DsdParagraphItem,
  DSDSubChunkInfo,
  DSDChunkInfo,
  PeriodType,
  IxdChunk,
  FsType,
  MatchedIxdCell,
  DsdHeaderTag,
  ChunkSummaryItem,
  ComparisonSummaryResponse,
  ChunkDetailResponse,
  IxdDsdCompareLogItem,
  PreviewDiffCell,
  PreviewDiffTable,
  PreviewDiffTablePair,
  PreviewDiffTocChunk,
  IxdPreviewDiffChunk,
} from "./types";

// --- API 함수 ---

// --- IXD report 기반 비교 API ---

/**
 * 신규 IXD 파일과 DSD 파일을 한 번에 업로드해 비교한다.
 * IXD 업로드와 비교를 서버에서 단일 요청으로 처리하는 fresh 흐름.
 */
export const uploadAndCompare = async (
  ixdFile: File,
  dsdFile: File,
): Promise<ComparisonSummaryResponse> => {
  const uploadId = await uploadIxdToStagingSlot(ixdFile);

  const completeForm = new FormData();
  completeForm.append("dsd_file", dsdFile);
  completeForm.append("ixd_upload_id", uploadId);
  completeForm.append("ixd_filename", ixdFile.name);

  const response = await request.post(
    "/v1/compare/upload-and-compare/complete",
    completeForm,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

/**
 * 기존 IXD report와 DSD를 비교한다.
 * 신규 IXD 파일은 먼저 `uploadIxdPreview`로 업로드해 reportId를 얻은 뒤 호출한다.
 */
export const compareFromReport = async (
  dsdFile: File,
  ixdReportId: string,
): Promise<ComparisonSummaryResponse> => {
  const formData = new FormData();
  formData.append("dsd_file", dsdFile);
  formData.append("ixd_report_id", ixdReportId);

  const response = await request.post(
    "/v1/compare/compare-from-report",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

export const getChunkDetail = async (
  comparisonId: string,
  chunkId: string,
  signal?: AbortSignal,
): Promise<ChunkDetailResponse> => {
  const response = await request.get(
    `/v1/compare/comparisons/${comparisonId}/chunks/${chunkId}`,
    { signal },
  );
  return response.data;
};

export const getComparisonHistory = async (
  limit: number = 20,
  offset: number = 0,
): Promise<IxdDsdCompareLogItem[]> => {
  const response = await request.get("/v1/compare/comparisons", {
    params: { limit, offset },
  });
  return response.data;
};

export const getComparisonSummary = async (
  comparisonId: string,
): Promise<ComparisonSummaryResponse> => {
  const response = await request.get(`/v1/compare/comparisons/${comparisonId}`);
  return response.data;
};

export type IxdPreviewCompareSourceType =
  | "xlsx"
  | "editable_treeview"
  | "ixd_report"
  | "ixd_file";

export interface IxdPreviewCompareSourceSpec {
  type: IxdPreviewCompareSourceType;
  workbookId?: string;
  xlsFile?: File;
  ixdReportId?: string;
  ixdFile?: File;
}

const appendSourceFields = (
  formData: FormData,
  side: "src" | "trg",
  spec: IxdPreviewCompareSourceSpec,
  ixdUploadId?: string,
) => {
  formData.append(`${side}_type`, spec.type);
  if (spec.type === "xlsx" && spec.xlsFile) {
    formData.append(`${side}_xls_file`, spec.xlsFile);
  } else if (spec.type === "editable_treeview" && spec.workbookId) {
    formData.append(`${side}_workbook_id`, spec.workbookId);
  } else if (spec.type === "ixd_report" && spec.ixdReportId) {
    formData.append(`${side}_ixd_report_id`, spec.ixdReportId);
  } else if (spec.type === "ixd_file" && ixdUploadId && spec.ixdFile) {
    // 백엔드는 blob 경로 대신 업로드 세션 id 만 받아 경로를 직접 구성한다
    // (임의 GCS 객체 접근 방지). 공유 스테이징 슬롯에 업로드돼 있다.
    formData.append(`${side}_ixd_upload_id`, ixdUploadId);
    formData.append(`${side}_ixd_filename`, spec.ixdFile.name);
  }
};

export const autoInputIxdFromDsd = async (
  priorDsd: File,
  priorIxd: File,
  currentDsd: File,
  currentIxd: File,
): Promise<{ blob: Blob; filename: string }> => {
  const formData = new FormData();
  formData.append("prior_dsd", priorDsd);
  formData.append("prior_ixd", priorIxd);
  formData.append("current_dsd", currentDsd);
  formData.append("current_ixd", currentIxd);

  const response = await request.post(
    "/v1/compare/auto-input-ixd-from-dsd",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
      responseType: "blob",
    },
  );

  const disposition = response.headers["content-disposition"] ?? "";
  const filenameMatch = disposition.match(/filename\*=UTF-8''(.+)/);
  const filename = filenameMatch
    ? decodeURIComponent(filenameMatch[1])
    : `auto_input_${currentIxd.name.replace(/\.ixd$/, "")}.zip`;

  return { blob: response.data, filename };
};

// --- IXD Preview Compare — NDJSON 스트리밍 (프론트 렌더용) ---

const parsePreviewDiffChunk = (line: string): IxdPreviewDiffChunk | null => {
  try {
    return JSON.parse(line) as IxdPreviewDiffChunk;
  } catch (error) {
    // 깨진 라인 하나가 전체 스트림을 무너뜨리지 않게 skip 한다.
    console.error("IXD preview diff 청크 파싱 실패:", error);
    return null;
  }
};

/**
 * IXD Preview 비교 결과를 NDJSON 스트림으로 받아 TOC 청크 단위로 yield 한다.
 *
 * 백엔드가 TOC 단위로 흘려보내므로 단일 응답을 가정하는 react-query 로는 점진
 * 소비가 불가능하다. fetch + ReadableStream 으로 직접 읽는 의도적 컨벤션 예외.
 * ixd_file 소스는 요청 크기 한도를 피하려고 먼저 스테이징 슬롯에 업로드한다.
 */
export async function* streamIxdPreviewCompare(
  src: IxdPreviewCompareSourceSpec,
  trg: IxdPreviewCompareSourceSpec,
  signal: AbortSignal,
  compareValues: boolean = true,
): AsyncGenerator<IxdPreviewDiffChunk> {
  const [srcUpload, trgUpload] = await Promise.all([
    src.type === "ixd_file" && src.ixdFile
      ? uploadIxdToStagingSlot(src.ixdFile)
      : Promise.resolve(null),
    trg.type === "ixd_file" && trg.ixdFile
      ? uploadIxdToStagingSlot(trg.ixdFile)
      : Promise.resolve(null),
  ]);

  const formData = new FormData();
  appendSourceFields(formData, "src", src, srcUpload ?? undefined);
  appendSourceFields(formData, "trg", trg, trgUpload ?? undefined);
  formData.append("compare_values", String(compareValues));

  const idToken = await getAuthIdToken();
  const headers: Record<string, string> = {};
  if (idToken) headers.Authorization = `Bearer ${idToken}`;

  const response = await fetch(
    new URL(
      "/v1/ixd-preview-compare/stream-diff-report",
      request.defaults.baseURL,
    ).toString(),
    { method: "POST", body: formData, headers, signal },
  );
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as
      | { error_code?: string; message?: string }
      | undefined;
    if (body?.error_code) {
      throw new ApiError(body.error_code, body.message ?? "", response.status);
    }
    throw new Error(`IXD preview compare 스트림 실패: ${response.status}`);
  }
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("응답 본문을 읽을 수 없습니다.");
  }

  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const chunk = parsePreviewDiffChunk(trimmed);
      if (chunk) yield chunk;
    }
  }
  const tail = buffer.trim();
  if (tail) {
    const chunk = parsePreviewDiffChunk(tail);
    if (chunk) yield chunk;
  }
}

/** IXD-DSD 대사 HTML 다운로드 집계용 no-op 호출(메인 서버). */
export const logIxdDsdCompareDownload = async (): Promise<void> => {
  await request.post("/v1/compare/logs/download");
};

/** IXD-IXD(Preview) 대사 HTML 다운로드 집계용 no-op 호출(메인 서버). */
export const logIxdPreviewCompareDownload = async (): Promise<void> => {
  await request.post("/v1/ixd-preview-compare/logs/download");
};
